import React, { FunctionComponent, ReactNode } from 'react';

import { CustomFieldKeyValueMap, DisplayMapEntry, KeyLabelPair } from '@components/bulk_management/automation/AutomationTypes';
import { PillSelector } from '@components/bulk_management/automation/form/PillSelector';
import { ListDropdown, ListOption, MultiselectDropdown } from '@components/library/dropdown';
import { StandardTextField } from '@components/library/text-field';
import { getAppropriateCustomFieldSelector } from '../helpers';
import { NameDisplayTypes } from '../AutomationEnums';

interface CustomFieldValuesSelectorProps {
  actionableId: string;
  associatedCustomFieldKey: string;
  error: ReactNode | undefined;
  fieldKeys: KeyLabelPair[];
  isMultiValuesAllowed: boolean;
  isRestricted: boolean;
  isSectionDisabled: boolean;
  selectedDisplayObject: DisplayMapEntry;
  updateCustomFieldValues: (customFieldKey: string, updatedCustomFieldValues: string[]) => void;
  valueAllowedOptions: ListOption[];
  values: CustomFieldKeyValueMap;
}

export const CustomFieldValuesSelector: FunctionComponent<CustomFieldValuesSelectorProps> = ({
  actionableId,
  associatedCustomFieldKey,
  error,
  fieldKeys,
  isMultiValuesAllowed,
  isRestricted,
  isSectionDisabled,
  selectedDisplayObject,
  updateCustomFieldValues,
  valueAllowedOptions,
  values
}) => {
  const appropriateCustomFieldValueSelector = getAppropriateCustomFieldSelector(
    !actionableId,
    isRestricted,
    isMultiValuesAllowed
  );

  return (
    <div className="selector-row__value">
      {appropriateCustomFieldValueSelector === NameDisplayTypes.MultiselectDropdown && (
        <MultiselectDropdown
          className="selector-values-input"
          disabled={!fieldKeys || isSectionDisabled}
          error={error}
          id="selector-values-input"
          label={<>{bfTranslate('Custom Field Values')}</>}
          onChange={(selectedOptions): void => {
            updateCustomFieldValues(associatedCustomFieldKey, selectedOptions as string[]);
          }}
          options={valueAllowedOptions}
          required
          searchable
          values={values?.[associatedCustomFieldKey]}
        />
      )}
      {appropriateCustomFieldValueSelector === NameDisplayTypes.PillSelector && (
        <PillSelector
          className="selector-values-input"
          error={error}
          handleCreate={(createdTag): void => {
            updateCustomFieldValues(
              associatedCustomFieldKey,
              [...values?.[associatedCustomFieldKey] || [], createdTag.name]
            );
          }}
          handleDelete={(_, i): void => {
            updateCustomFieldValues(
              associatedCustomFieldKey,
              [...values?.[associatedCustomFieldKey].filter((value, j) => (i !== j)) || []]
            );
          }}
          id="selector-values-input"
          label={<>{bfTranslate('Custom Field Values')}</>}
          required
          selectedValues={values?.[associatedCustomFieldKey]}
          suggestedValues={selectedDisplayObject?.nameOptions?.map((option: ListOption) => (option.value.toString()))}
        />
      )}
      {appropriateCustomFieldValueSelector === NameDisplayTypes.ListDropdown && (
        <ListDropdown
          className="selector-values-input"
          error={error}
          id="selector-values-input"
          label={<>{bfTranslate('Custom Field Value')}</>}
          onChange={(selectedOption: ListOption): void => {
            updateCustomFieldValues(associatedCustomFieldKey, [selectedOption.value.toString()]);
          }}
          options={valueAllowedOptions}
          placeholder={bfTranslate('Select custom field value')}
          required
          value={values?.[associatedCustomFieldKey]?.[0]}
        />
      )}
      {appropriateCustomFieldValueSelector === NameDisplayTypes.Textfield && (
        <StandardTextField
          aria-required
          className="selector-values-input"
          disabled={!fieldKeys || isSectionDisabled}
          error={error}
          id="selector-values-input"
          label={<>{bfTranslate('Custom Field Value')}</>}
          onChange={(e: InputChangeEvent): void => {
            updateCustomFieldValues(associatedCustomFieldKey, [e.target.value]);
          }}
          onKeyPress={(e: InputKeyboardEvent): void => {
            if (e.key === 'Enter') {
              // prevent typing "Enter" from submitting the form
              e.preventDefault();
            }
          }}
          placeholder={bfTranslate('Enter custom field value')}
          required
          value={!values ? '' : values[associatedCustomFieldKey]?.[0]}
        />
      )}
    </div>
  );
};
